import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Box, Users, Settings,
  FileText, LogOut, Menu, DollarSign, Sparkles, Download,
  Briefcase, Calendar, TrendingUp, BarChart3, UserCircle,
  ChevronDown, ChevronRight, Building2, Shield, Globe, Store
} from 'lucide-react';
import ThemeToggle from './ThemeToggle';

// NavLink component - outside of DashboardLayout
const NavLink = ({ item, indent = false, isActive, onClick }) => {
  return (
    <Link
      to={item.path}
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
        indent ? 'ml-4' : ''
      } ${
        isActive
          ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/20'
          : 'text-gray-400 hover:bg-gray-800 hover:text-white'
      }`}
    >
      <item.icon className={`w-5 h-5 ${isActive ? 'text-orange-400' : ''}`} />
      <span className="font-medium text-sm">{item.label}</span>
    </Link>
  );
};

// AccordionSection component - outside of DashboardLayout
const AccordionSection = ({ title, icon: Icon, isOpen, onToggle, hasActiveChild, children }) => {
  return (
    <div className="space-y-1">
      <button
        onClick={onToggle}
        className={`flex items-center justify-between w-full px-4 py-3 rounded-lg transition-colors ${
          hasActiveChild && !isOpen
            ? 'bg-blue-600/20 text-blue-400'
            : 'text-gray-300 hover:bg-gray-800 hover:text-white'
        }`}
      >
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 ${hasActiveChild ? 'text-orange-400' : ''}`} />
          <span className="font-semibold text-sm uppercase tracking-wide">{title}</span>
        </div>
        {isOpen ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
      </button>
      
      {isOpen && (
        <div className="space-y-1 pb-2">
          {children}
        </div>
      )}
    </div>
  );
};

const DashboardLayout = ({ children }) => {
  const { user, logout, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Check if user is platform admin
  const isPlatformAdmin = user?.tenant_role === 'platform_admin' || 
                          user?.role_id === 'role_platform_admin' ||
                          user?.role_id === 'role_developer';
  
  // Accordion state - open by default based on current path
  const [crmOpen, setCrmOpen] = useState(location.pathname.startsWith('/crm') || location.pathname === '/dashboard');
  const [adminOpen, setAdminOpen] = useState(
    ['/pricing', '/configurator-settings', '/import-export', '/analysis', '/users', '/roles'].some(p => location.pathname.startsWith(p))
  );
  const [platformOpen, setPlatformOpen] = useState(location.pathname.startsWith('/platform-admin'));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeSidebar = () => setSidebarOpen(false);

  // Top level items (not in accordion)
  const topItems = [
    { path: '/demo', icon: Box, label: 'Configurator', permission: null },
    { path: '/leads', icon: FileText, label: 'Leads', permission: 'perm_leads_read' },
  ];

  // CRM section items
  const crmItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', permission: null },
    { path: '/crm/customers', icon: UserCircle, label: 'Customers', permission: 'perm_leads_read' },
    { path: '/crm/opportunities', icon: TrendingUp, label: 'Pipeline', permission: 'perm_leads_read' },
    { path: '/crm/projects', icon: Briefcase, label: 'Projects', permission: 'perm_leads_read' },
    { path: '/crm/bookings', icon: Calendar, label: 'Scheduling', permission: 'perm_leads_read' },
    { path: '/crm/reports', icon: BarChart3, label: 'Reports', permission: 'perm_leads_read' },
  ];

  // Admin section items
  const adminItems = [
    { path: '/users', icon: Users, label: 'Users', permission: 'perm_users_read' },
    { path: '/roles', icon: Shield, label: 'Roles & Permissions', permission: 'perm_roles_read' },
    { path: '/pricing', icon: DollarSign, label: 'Pricing', permission: 'perm_roles_read' },
    { path: '/billing-settings', icon: DollarSign, label: 'Billing & Stripe', permission: null },
    { path: '/configurator-settings', icon: Settings, label: 'Configurator Settings', permission: 'perm_roles_read' },
    { path: '/import-export', icon: Download, label: 'Import / Export', permission: 'perm_roles_read' },
    { path: '/analysis', icon: Sparkles, label: 'AI Analysis', permission: 'perm_analysis_create' },
  ];

  // Platform Admin section items (only visible to platform admins)
  const platformItems = [
    { path: '/platform-admin', icon: Globe, label: 'Platform Dashboard', permission: null },
    { path: '/plan-management', icon: DollarSign, label: 'Subscription Plans', permission: null },
    { path: '/platform-settings', icon: Settings, label: 'Platform Settings', permission: null },
    { path: '/builder/settings', icon: Building2, label: 'Builder Branding', permission: null },
    { path: '/sales-company/settings', icon: Store, label: 'Sales Co. Branding', permission: null },
  ];

  const filterItems = (items) => {
    // Platform admins see all items
    if (isPlatformAdmin) {
      return items;
    }
    return items.filter(
      item => !item.permission || hasPermission(item.permission)
    );
  };

  const filteredTopItems = filterItems(topItems);
  const filteredCrmItems = filterItems(crmItems);
  const filteredAdminItems = filterItems(adminItems);

  const crmHasActiveChild = filteredCrmItems.some(item => location.pathname === item.path);
  const adminHasActiveChild = filteredAdminItems.some(item => location.pathname === item.path);

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-950 transition-colors duration-300">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 dark:bg-gray-900 transform transition-transform duration-300 lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex items-center justify-center px-6 py-5 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold text-white">
              Fusion<span className="text-orange-500">Builder</span>Pro
            </span>
          </div>
        </div>

        <nav className="px-3 py-4 space-y-2 overflow-y-auto max-h-[calc(100vh-200px)]">
          {/* Top level items */}
          {filteredTopItems.map((item) => (
            <NavLink 
              key={item.path} 
              item={item} 
              isActive={location.pathname === item.path}
              onClick={closeSidebar}
            />
          ))}

          {/* CRM Accordion */}
          {filteredCrmItems.length > 0 && (
            <AccordionSection
              title="CRM"
              icon={Building2}
              isOpen={crmOpen}
              onToggle={() => setCrmOpen(!crmOpen)}
              hasActiveChild={crmHasActiveChild}
            >
              {filteredCrmItems.map((item) => (
                <NavLink 
                  key={item.path} 
                  item={item} 
                  indent 
                  isActive={location.pathname === item.path}
                  onClick={closeSidebar}
                />
              ))}
            </AccordionSection>
          )}

          {/* Admin Accordion */}
          {filteredAdminItems.length > 0 && (
            <AccordionSection
              title="Admin"
              icon={Settings}
              isOpen={adminOpen}
              onToggle={() => setAdminOpen(!adminOpen)}
              hasActiveChild={adminHasActiveChild}
            >
              {filteredAdminItems.map((item) => (
                <NavLink 
                  key={item.path} 
                  item={item} 
                  indent 
                  isActive={location.pathname === item.path}
                  onClick={closeSidebar}
                />
              ))}
            </AccordionSection>
          )}

          {/* Platform Admin Section - Only for Platform Admins */}
          {isPlatformAdmin && (
            <AccordionSection
              title="Platform"
              icon={Globe}
              isOpen={platformOpen}
              onToggle={() => setPlatformOpen(!platformOpen)}
              hasActiveChild={location.pathname.startsWith('/platform-admin')}
            >
              {platformItems.map((item) => (
                <NavLink 
                  key={item.path} 
                  item={item} 
                  indent 
                  isActive={location.pathname === item.path}
                  onClick={closeSidebar}
                />
              ))}
            </AccordionSection>
          )}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800 bg-gray-900/95">
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center ring-2 ring-orange-500/50">
              <span className="text-white font-semibold">
                {user?.name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.name}</p>
              <p className="text-orange-400 text-sm truncate">{user?.role_name}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-3 mt-2 text-gray-400 hover:bg-red-600/20 hover:text-red-400 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="bg-white dark:bg-gray-900 shadow-sm dark:shadow-gray-800/50 sticky top-0 z-30 transition-colors duration-300">
          <div className="flex items-center justify-between px-4 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex-1" />
            <div className="flex items-center gap-4">
              {/* Theme Toggle */}
              <ThemeToggle />
              
              <span className="text-sm text-gray-500 dark:text-gray-400 hidden sm:block">
                Welcome, {user?.name}
              </span>
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center ring-2 ring-orange-500/30">
                <span className="text-white text-sm font-semibold">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
